/**
 * O "⋯" no fim da barra de abas do projeto: Projeto, Cadastros, Sistemas,
 * Modelos e Checklist.
 *
 * A produção pediu a barra só com as abas de cadastrar conteúdo e o resto num
 * "⋯" ali mesmo, ao lado delas (relatório de 28/09/2026) — não no menu do topo,
 * onde ficariam longe do projeto a que pertencem.
 *
 * **Fechado, é só o "⋯".** Quando a tela aberta é uma destas, o botão vira o nome
 * dela com uma setinha ("Checklist ▾"), aceso como uma aba: sem isso a barra não
 * teria aba acesa e ninguém saberia onde está.
 */

import { Link } from "react-router-dom"
import * as Menu from "@radix-ui/react-dropdown-menu"
import type { ItemDeMenu } from "@/casca/navegacao"
import { alturaMaximaFlutuante, escalaFlutuante } from "@/kit/flutuante"

export function MaisDoProjeto({ projetoId, itens, aberto }: {
  projetoId: string | number
  /** Já filtrados pela permissão. `para` é o trecho depois de `/projetos/:id/`. */
  itens: ItemDeMenu[]
  /** O item cuja tela está aberta agora, se for um destes. */
  aberto?: ItemDeMenu
}) {
  if (itens.length === 0) return null

  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={aberto ? `${aberto.rotulo} — mais telas do projeto` : "Mais telas do projeto"}
        style={{
          display: "flex", alignItems: "center", gap: 6, padding: "0 12px", fontSize: 12.5,
          fontWeight: aberto ? 600 : 500,
          color: aberto ? "var(--txt)" : "var(--txt2)",
          boxShadow: aberto ? "inset 0 -2px 0 var(--brand)" : "none",
        }}
      >
        {aberto ? (
          <>
            {aberto.rotulo}
            {/* A setinha, como no "Mais ▾" do topo: diz que ali se escolhe outra. */}
            <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
                 style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" }}>
              <path d="m6 9.5 6 6 6-6" />
            </svg>
          </>
        ) : (
          <span aria-hidden="true" style={{ fontSize: 15, lineHeight: 1, letterSpacing: 1 }}>⋯</span>
        )}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          sideOffset={2} align="start"
          // Escolhida a tela, a navegação troca de página: devolver o foco ao botão
          // só deixava o contorno de foco aceso nele.
          onCloseAutoFocus={(e) => e.preventDefault()}
          style={{
            minWidth: 190, padding: 6, borderRadius: 10,
            background: "var(--surface)", border: "1px solid var(--line2)",
            boxShadow: "var(--sombra)", color: "var(--txt)", zIndex: 50,
            maxHeight: alturaMaximaFlutuante(400), overflowY: "auto",
            ...escalaFlutuante,
          }}
        >
          {itens.map((item) => {
            const atual = aberto?.para === item.para
            return (
              <Menu.Item key={item.para} asChild>
                <Link
                  to={`/projetos/${projetoId}/${item.para}`}
                  style={{
                    display: "block", padding: "7px 9px", borderRadius: 6, fontSize: 12.5,
                    color: "var(--txt)", textDecoration: "none", outline: "none",
                    fontWeight: atual ? 600 : 400,
                    background: atual ? "var(--brand-soft)" : undefined,
                  }}
                >
                  {item.rotulo}
                </Link>
              </Menu.Item>
            )
          })}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  )
}
